import { isNonNullable } from '../../types';
import { TABLE_OPTIONS_QUERY_PARAMETER } from '../TableQueryOptions';
import type { FilterValue, Filters } from '../filters/filters';

/**
 * Props passed to react-router-dom's <Link> component
 *
 * This must be a subtype of LinkProps from react-router-dom, apart from the
 * explicitly listed presentation flags below. We can't (and don't
 * even want to) declare the full type there without importing react-router-dom types,
 * so only a reasonable subset of features will be supported there. Feel free to extend
 * it at any time needed.
 */
export interface TableLinkProps {
  to: string;
  state?: unknown;
  /**
   * Render the cell link without the trailing link glyph.
   *
   * Presentation, not a router prop — the table's link renderer consumes it and
   * never forwards it on. It rides here rather than on the column format so that
   * every cell renderer honours it through the one shared link wrapper, instead
   * of each having to remember to pass it.
   */
  hideIcon?: boolean;
}

/**
 * Definition for input to create*Column functions
 */
export interface TableLink<TRow> {
  createLinkProps: (
    row: TRow,
    currentValue?: FilterValue,
  ) => TableLinkProps | undefined;
  dependencies: (keyof TRow)[];
}

/**
 * Extension to input of create*Column functions that support links
 */
export interface TableLinkColumnConfig<TRow> {
  /**
   * Definition of a link on table cell
   *
   * Due to robust typing that ensures all required fields are fetched,
   * it is not possible to define this in an inline object. Use `createTableLink`
   * or some derivative to create & properly type check a link.
   */
  link?: TableLink<TRow>;
}

/**
 * Signature for createLinkProps to be used in table implementation
 *
 * This is a simplified generic signature without type checking that the function
 * uses only listed dependencies from TRow; that is important only at definition
 * of the link and would involve unnecessary type gymnastics in table implementation.
 *
 * @see CreateLinkPropsTypesafeFunction
 */
export type CreateLinkPropsFunction<TRow = unknown> = (
  row: TRow,
  currentValue?: FilterValue,
) => TableLinkProps | undefined;

/**
 * Signature for createLinkProps to be used for createTableLink and derivatives
 *
 * This signature type checks that the function uses only fields listed in dependencies.
 * This should be checked at definition of the link.
 *
 * @see CreateLinkPropsFunction
 */
export type CreateLinkPropsTypesafeFunction<
  TRow,
  TDependencies extends keyof TRow,
> = (row: Pick<TRow, TDependencies>) => TableLinkProps | undefined;

/**
 * Extension of TableColumnFormat fields for columns that support links
 */
export interface TableColumnFormatLink<TRow> {
  /**
   * Create props for react-router-dom's <Link> component
   *
   * No need to type check dependencies there, that is already processed by createTableLink
   */
  createLinkProps?: CreateLinkPropsFunction<TRow>;
}

const removeNullableFilters = (filters: Filters): Filters =>
  Object.entries(filters).reduce<Filters>((acc, [key, filter]) => {
    if (filter && isNonNullable(filter[filter.length - 1])) {
      acc[key] = filter;
    }

    return acc;
  }, {});

export const createTableLink = <TRow, TDependencies extends keyof TRow>(
  createLinkProps: CreateLinkPropsTypesafeFunction<TRow, TDependencies>,
  /**
   * List of fields required to render the link
   *
   * The base column that the link is rendered on has to be listed there too,
   * if it is needed to render the link. It is technically redundant, but we need it
   * to satisfy the robust type check whether all used fields are listed there.
   */
  dependencies: TDependencies[],
): TableLink<TRow> => ({ createLinkProps, dependencies });

export const createTableFilterLink = <TRow, TDependencies extends keyof TRow>(
  tableWebPath: string,
  createFilter: (
    row: Pick<TRow, TDependencies>,
    currentValue?: FilterValue,
  ) => Filters,
  dependencies: TDependencies[],
  hideIcon?: boolean,
): TableLink<TRow> =>
  createTableLink(
    (row, currentValue?: FilterValue) =>
      createTableFilterLinkProps(
        tableWebPath,
        createFilter,
        row,
        currentValue,
        hideIcon,
      ),
    dependencies,
  );

export const createTableFilterLinkProps = <TRow>(
  tableWebPath: string,
  createFilter: (row: TRow, currentValue?: FilterValue) => Filters,
  row: TRow,
  currentValue?: FilterValue,
  hideIcon?: boolean,
): TableLinkProps => {
  const filters = removeNullableFilters(createFilter(row, currentValue));

  return {
    to: `${tableWebPath}?${TABLE_OPTIONS_QUERY_PARAMETER}=${encodeURIComponent(
      JSON.stringify({ filters }),
    )}`,
    ...(hideIcon ? { hideIcon } : {}),
  };
};
